import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import './HelpPage.css';

var faqData = [
  {
    category: 'Buying',
    icon: 'shopping-bag',
    delay: '1',
    questions: [
      {
        q: 'How do I buy a card?',
        a: 'Browse or search for the card you want, click on a listing, and add it to your cart. When you\'re ready, go to your cart and checkout with a credit card or PayPal.'
      },
      {
        q: 'Can I buy from multiple sellers in one checkout?',
        a: 'Yes! Our cart supports multi-seller checkout. Add cards from different sellers and pay for everything at once. Items are grouped by seller for shipping.'
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit and debit cards (via Stripe) and PayPal.'
      },
      {
        q: 'Is there buyer protection?',
        a: 'Yes. All purchases are protected by our 14-day buyer protection program. Funds are held in escrow until you confirm receipt of your card, or until the 14-day period expires.'
      },
      {
        q: 'What if I receive a damaged or wrong card?',
        a: 'You can open a dispute from your Order History within 14 days of the order being shipped. Our team will review the case and issue a refund if appropriate.'
      }
    ]
  },
  {
    category: 'Selling',
    icon: 'tag',
    delay: '2',
    questions: [
      {
        q: 'How do I list a card for sale?',
        a: 'Click "Sell" in the navigation bar, search for your card, set your price and condition, upload photos, and publish. You can also use Bulk Sell to list multiple cards at once.'
      },
      {
        q: 'What fees does Primal Market charge?',
        a: 'You keep 90% of your item price + all shipping. On orders under $9, your payout may be slightly lower to cover processing costs. We never take a cut of shipping.'
      },
      {
        q: 'Can I accept offers from buyers?',
        a: 'Yes. When creating a listing, toggle "Open to Offers" to let buyers negotiate a price. You can accept, decline, or counter any offer.'
      },
      {
        q: 'How do I edit or delete a listing?',
        a: 'Go to your shop page (click your profile picture → your shop). Click on any of your listings and use the edit or delete buttons.'
      },
      {
        q: 'What is the minimum listing price?',
        a: 'The minimum listing price is $0.75.'
      },
      {
        q: 'Why is my payout slightly less than 90% on a cheap card?',
        a: 'Transparency matters to us, so here\'s the full picture: we try to have the buyer cover the brunt of payment processing fees through a small checkout fee — so you don\'t have to. However, on orders under $9, processing costs can actually exceed what we earn from the 10% platform fee. Rather than hide this, we show you the exact breakdown in your Sales tab. To keep the marketplace running and free to list on, we apply a very small fee to cover the difference in those cases — typically less than $0.50. If a buyer purchases multiple cards from you in one order, the totals combine and this almost never applies. We believe in being upfront about how fees work — no hidden charges, no surprises.',
        anchor: 'payout-fees'
      }
    ]
  },
  {
    category: 'Payments & Payouts',
    icon: 'usd',
    delay: '3',
    questions: [
      {
        q: 'How do I get paid?',
        a: 'Connect your Stripe account in Settings → Payments. After a buyer confirms receipt (or after the 14-day hold), your earnings are transferred to your Stripe account and then to your bank.'
      },
      {
        q: 'How long does it take to get paid?',
        a: 'After the buyer confirms receipt (or 14 days after shipping), payouts are released to Stripe. Stripe typically deposits to your bank within 2 business days.'
      },
      {
        q: 'Can I get paid via PayPal?',
        a: 'For orders paid with PayPal, your payout is sent to the PayPal email you have on file in your settings. For card orders, payouts go through Stripe.'
      },
      {
        q: 'Why is my payout on hold?',
        a: 'Payouts are held for up to 14 days after shipping as part of our buyer protection program. If the buyer confirms receipt early, the hold is released sooner.'
      },
      {
        q: 'What if my payout fails?',
        a: 'If a payout fails (e.g., disconnected Stripe account), it will show as "Payout Pending" in your Order History. Reconnect your payment method in Settings and the payout will be retried.'
      }
    ]
  },
  {
    category: 'Shipping',
    icon: 'truck',
    delay: '4',
    questions: [
      {
        q: 'How do I ship a card?',
        a: 'After a sale, you\'ll receive a notification. Package the card safely (we recommend a top loader in a bubble mailer) and ship it. Add the tracking number to the order from your Order History.'
      },
      {
        q: 'How does shipping pricing work?',
        a: 'Orders $10 and above qualify for free shipping. Orders $50 and above require tracked shipping (included automatically). For orders under $50, buyers can add a tracking upgrade for $5.50. Orders under $10 have a flat $1.00 shipping fee.'
      },
      {
        q: 'What shipping carriers can I use?',
        a: 'You can use any carrier (USPS, UPS, FedEx, etc.) as long as you provide a valid tracking number.'
      },
      {
        q: 'Who pays for shipping?',
        a: 'For orders under $10, the buyer pays a flat $1.00 shipping fee. Orders $10 and above ship free. Orders $50 and above include tracked shipping automatically. Sellers receive the full shipping amount — the platform fee only applies to the card price.'
      }
    ]
  },
  {
    category: 'Account',
    icon: 'user-circle',
    delay: '5',
    questions: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" and enter your email and password. You\'ll receive a verification email — click the link to activate your account.'
      },
      {
        q: 'How do I reset my password?',
        a: 'Click "Sign In", then "Forgot Password". Enter your email and we\'ll send you a password reset link.'
      },
      {
        q: 'Can I change my username?',
        a: 'Currently, usernames cannot be changed after account creation. Contact support if you need assistance.'
      },
      {
        q: 'How do I close my shop temporarily?',
        a: 'Go to your profile and click "Close Shop". Your listings will be hidden from search and browse. You can reopen at any time.'
      }
    ]
  }
];

class FAQ extends Component {
  constructor(props) {
    super(props);
    this.state = { isDark: document.documentElement.classList.contains('tw-dark'), showScrollTop: false, openItems: {} };
    this._observer = null;
    this._darkObserver = null;
    this._handleScroll = this._handleScroll.bind(this);
  }

  componentDidMount() {
    var self = this;
    var hash = window.location.hash.replace('#', '');

    // Check if hash matches a specific question anchor
    var anchorMatch = null;
    faqData.forEach(function(category, catIdx) {
      category.questions.forEach(function(item, qIdx) {
        if (item.anchor && item.anchor === hash) {
          anchorMatch = { key: catIdx + '-' + qIdx, anchor: hash };
        }
      });
    });

    if (anchorMatch) {
      // Auto-open the matched question
      var openItems = {};
      openItems[anchorMatch.key] = true;
      this.setState({ openItems: openItems }, function() {
        setTimeout(function() {
          var el = document.getElementById(anchorMatch.anchor);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 400);
      });
    } else if (!hash) {
      window.scrollTo(0, 0);
    } else {
      // Category-level anchor (e.g. #selling)
      setTimeout(function() {
        var el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }

    var els = document.querySelectorAll('.help-reveal');
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('help-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
      els.forEach(function(el) { observer.observe(el); });
      this._observer = observer;
    } else {
      els.forEach(function(el) { el.classList.add('help-visible'); });
    }

    window.addEventListener('scroll', this._handleScroll);
    this._darkObserver = new MutationObserver(function() {
      var dark = document.documentElement.classList.contains('tw-dark');
      if (dark !== self.state.isDark) self.setState({ isDark: dark });
    });
    this._darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  componentWillUnmount() {
    if (this._observer) this._observer.disconnect();
    if (this._darkObserver) this._darkObserver.disconnect();
    window.removeEventListener('scroll', this._handleScroll);
  }

  _handleScroll() {
    var show = window.scrollY > 400;
    if (show !== this.state.showScrollTop) this.setState({ showScrollTop: show });
  }

  toggleItem(key) {
    this.setState(function(prev) {
      var next = Object.assign({}, prev.openItems);
      next[key] = !next[key];
      return { openItems: next };
    });
  }

  render() {
    var isDark = this.state.isDark;
    var openItems = this.state.openItems;
    var toggleItem = this.toggleItem.bind(this);

    return (
      <div style={{ backgroundColor: isDark ? '#111' : '#f9fafb', minHeight: '100vh' }}>
        <Helmet><title>FAQ | Primal Market</title></Helmet>

        {/* Hero */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          padding: '60px 20px 48px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{
              width: '56px', height: '56px', borderRadius: '16px',
              backgroundColor: 'rgba(201,168,76,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <i className="fa fa-question-circle" style={{ color: '#c9a84c', fontSize: '24px' }} />
            </div>
            <h1 style={{ color: '#fff', fontSize: '32px', fontWeight: '700', marginBottom: '12px', marginTop: 0 }}>
              Frequently Asked Questions
            </h1>
            <p style={{ color: '#9ca3af', fontSize: '16px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
              Everything you need to know about buying and selling on Primal Market.
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 16px 64px' }}>

          {faqData.map(function(category, catIdx) {
            return (
              <div key={catIdx} id={category.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')} className={'help-reveal help-fade-up help-delay-' + category.delay} style={{ marginBottom: '28px' }}>
                {/* Category Header */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    backgroundColor: 'rgba(201,168,76,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <i className={'fa fa-' + category.icon} style={{ color: '#c9a84c', fontSize: '15px' }} />
                  </div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: isDark ? '#fff' : '#1a1a1a' }}>
                    {category.category}
                  </h2>
                </div>

                {/* Questions */}
                <div className="help-section-card" style={{
                  backgroundColor: isDark ? '#141414' : '#fff',
                  border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8'),
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                }}>
                  {category.questions.map(function(item, qIdx) {
                    var key = catIdx + '-' + qIdx;
                    var isOpen = !!openItems[key];
                    var isLast = qIdx === category.questions.length - 1;
                    return (
                      <div key={qIdx} id={item.anchor || undefined} style={{
                        borderBottom: isLast ? 'none' : ('1px solid ' + (isDark ? '#2a2a2a' : '#f0f0f0'))
                      }}>
                        <button
                          onClick={function() { toggleItem(key); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', padding: '16px 20px',
                            backgroundColor: 'transparent', border: 'none',
                            cursor: 'pointer', textAlign: 'left',
                            color: isDark ? '#fff' : '#1a1a1a',
                            fontSize: '15px', fontWeight: '500',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = isDark ? '#1a1a1a' : '#fafafa'; }}
                          onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <span style={{ paddingRight: '16px' }}>{item.q}</span>
                          <i className={'fa fa-chevron-' + (isOpen ? 'up' : 'down')}
                            style={{ color: '#c9a84c', fontSize: '12px', flexShrink: 0, transition: 'transform 0.2s ease' }} />
                        </button>
                        <div style={{
                          maxHeight: isOpen ? '300px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease'
                        }}>
                          <div style={{
                            padding: '0 20px 16px',
                            color: isDark ? '#d1d5db' : '#4b5563',
                            fontSize: '14px', lineHeight: '1.7'
                          }}>
                            {item.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Still need help */}
          <div className="help-reveal help-fade-up help-delay-6" style={{
            backgroundColor: isDark ? '#1a1a1a' : '#f0f4ff',
            borderRadius: '16px',
            padding: '28px',
            textAlign: 'center',
            border: '1px solid ' + (isDark ? '#2a2a2a' : '#dce3f5'),
            marginTop: '12px'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: isDark ? '#fff' : '#1a1a1a' }}>
              Still have questions?
            </h3>
            <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '14px', margin: '0 0 16px' }}>
              Reach out to us on Discord or send an email — we're happy to help.
            </p>
            <Link to="/contact" className="tw-no-underline help-cta-btn" style={{
              display: 'inline-block', backgroundColor: '#c9a84c', color: '#000',
              fontWeight: '600', fontSize: '14px', padding: '10px 24px',
              borderRadius: '8px', textDecoration: 'none'
            }}>
              Contact Us
            </Link>
          </div>
        </div>

        {this.state.showScrollTop && (
          <button
            onClick={function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default FAQ;
